import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { setTitleBar } from "store/titleBar.slice";
import { Button } from "react-bootstrap";
import { IoClose } from "react-icons/io5";
import { MdOutlineFullscreen, MdOutlineFullscreenExit } from "react-icons/md";
import Loader from "components/loader/loader";
import { useTranslation } from "react-i18next";
import "./applications.scss";
import { images } from 'assets';
import applicationsJSON from 'pages/applications/applications.json'

interface applicationType {
    id: string;
    name: string;
    description: string;
    imageSrc: string;
    url: string;
}

const Applications = () => {
    const dispatch = useDispatch();
    const [applications, setApplications] = useState<applicationType[]>([]);
    const [buttonTitle, setButtonTitle] = useState<string>("");
    const [selectedApp, setSelectedApp] = useState<applicationType>();
    const [isFullScreenApp, setIsFullScreenApp] = useState<boolean>(false);
    const iframeContainerRef = useRef<HTMLDivElement>(null);
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        setApplications(applicationsJSON);

        dispatch(setTitleBar({ title: "title_applications", data: [], filteredData: [] }));

        const handleChange = () => {
            setIsFullScreenApp(!!document.fullscreenElement);
        };

        document.addEventListener("fullscreenchange", handleChange);

        return () => {
            dispatch(setTitleBar({ title: "", data: [], filteredData: [] }));
            document.removeEventListener("fullscreenchange", handleChange);
            setIsLoading(false);
            setIsFullScreenApp(false);
            setSelectedApp(undefined);
        }
    }, [])

    useEffect(() => {
        setIsLoading(true);
        iframeContainerRef.current?.focus();
    }, [selectedApp])

    useEffect(() => {
        if(!selectedApp) return;
        setButtonTitle(
            isFullScreenApp ? 
                `${t('reduce_screen')} ${selectedApp.name}` : 
                `${t('full_screen_1')} ${selectedApp.name} ${t('full_screen_2')}`
        );
    }, [selectedApp, isFullScreenApp])

    const toggleFullscreen = async () => {
        const el = iframeContainerRef.current;

        if (!el) return;

        if (!document.fullscreenElement) {
            await el.requestFullscreen();
            setIsFullScreenApp(true);
        } else {
            await document.exitFullscreen();
            setIsFullScreenApp(false);
        }
    };

    const handleIframeLoad = () => {
        setIsLoading(false);
    };

    return (
        <div className="applications">
            {applications.map(app => (
                <div
                    key={app.id}
                    tabIndex={0}
                    className={`applicationCard ${selectedApp ? "reducedCard" : ""}`}
                    onClick={() => setSelectedApp(app)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelectedApp(app);
                        }
                    }}
                >
                    <div className="applications__logo">
                        <img src={images[app.imageSrc]} alt={app.name} />
                    </div>
                    <h3>{app.name}</h3>
                    <p>{t(app.description)}</p>
                </div>
            ))}
            {selectedApp && (
                <div className="iframeContainer" tabIndex={-1} ref={iframeContainerRef}>
                    <Button
                        variant="light"
                        title={buttonTitle}
                        aria-label={buttonTitle}
                        className="iframeButton fullScreenButton"
                        onClick={toggleFullscreen}>
                        {isFullScreenApp ? <MdOutlineFullscreenExit className="centered" /> : <MdOutlineFullscreen className="centered" />}
                    </Button>
                    <Button
                        variant="light"
                        title={`${t('close')} ${selectedApp.name}`}
                        aria-label={`${t('close')} ${selectedApp.name}`}
                        className="iframeButton closeButton"
                        onClick={() => setSelectedApp(undefined)}>
                        <IoClose className="centered" />
                    </Button>

                    <iframe
                        className={[
                            isFullScreenApp && "fullscreen",
                            isLoading && "loading"
                        ].filter(Boolean).join(" ")}
                        src={selectedApp.url}
                        onLoad={handleIframeLoad}
                        title={selectedApp.name}
                        role="application"
                        allow="microphone"
                        sandbox="allow-scripts allow-same-origin"
                    />
                    {isLoading && (
                        <Loader className="loader_tertiary centered" />
                    )}
                </div>
            )}

        </div>
    );
};

export default Applications;